import React, { useState, useEffect } from 'react';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { ComplementaryActivityCard } from './complementary-activity-card';
import { useSession } from 'src/app/hooks/useSession';
import { useClient } from 'jsonapi-react';
import useAuthorityLevel from '@/app/hooks/useAuthorityLevel';
import toast from 'react-hot-toast';
import ActivityForm from './ActivityForm';
import Onboarding from 'src/app/components/Onboarding';
import { Filter, FilterActions, FilterCheck, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import usePermission from '@/app/hooks/usePermission';
import { DashboardContent } from '@/layouts/dashboard';
import { FiSearch } from 'react-icons/fi';
import { Button } from '@/components/ui/button';
import { CardWrapper } from '@/components/ui/card';
import { Sheet, SheetBody, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

export function ComplementaryActivity(props) {
  const { uri } = props;

  const { t } = useTranslation();
  const { session } = useSession();
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];
  const path = props.path;
  const client = useClient();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [editingActivity, setEditingActivity] = useState();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const authorityLevel = useAuthorityLevel();
  const [relationships, setRelationships] = useState({
    courses: [],
    classrooms: []
  });

  const filters = useFormik({
    initialValues: {
      course: 'blank',
      classroom: 'blank',
      'activity-status': false
    }
  });

  const getRelationships = async () => {
    const { data: courses } = await client.fetch('courses');
    const { data: classrooms } = await client.fetch('classrooms');

    setRelationships({
      courses: courses || [],
      classrooms: classrooms || []
    });
  };

  const getActivities = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('activities');
    if (error) {
      toast.error(t('toast.errorFetchComplementActivity'));
    } else {
      setActivities(data);
      setLoading(false);
    }
  };

  const deleteActivity = async id => {
    setLoading(true);
    const { error } = await client.delete(['activities', id]);
    if (error) {
      toast.error(t('toast.errorDeleteActivity'));
    } else {
      getActivities();
      toast.success(t('toast.successDeleteActivity'));
      setLoading(false);
    }
  };

  const newActivity = () => {
    setEditingActivity({});
    setShowModal(true);
  };

  const editActivity = activity => {
    setEditingActivity(activity);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingActivity(null);
  };

  function filterActivitiesByCourse(id, activities) {
    return activities.filter(function (activity) {
      return activity['course-id'] === id;
    });
  }

  function filterActivitiesByClassroom(id, activities) {
    return activities.filter(function (activity) {
      return activity['classroom-id'] === id;
    });
  }

  useEffect(() => {
    getActivities();
    getRelationships();
  }, []);

  useEffect(() => {
    let newActivities = activities;
    if (filters.values.course !== 'blank') {
      const id = +filters.values.course;
      newActivities = filterActivitiesByCourse(id, activities);
    }
    if (filters.values.classroom !== 'blank') {
      const id = +filters.values.classroom;
      newActivities = filterActivitiesByClassroom(id, activities);
    }
    if (searchTerm !== '') {
      newActivities = newActivities.filter(a => a.title != null && a.title.toLowerCase().includes(searchTerm.toLocaleLowerCase()));
    }
    if (filters.values['activity-status']) {
      newActivities = newActivities.filter(activity => activity['activity-status-or-submitted-count'] !== null);
    }
    setFilteredActivities(newActivities);
  }, [filters.values, searchTerm, activities]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Atividade Complementar',
        isActive: true
      }
    ]
  };

  const createSyntheticChangeEvent = (checked, name, value) => {
    // Cria o objeto 'target' com as propriedades que o Formik espera.
    return {
      target: {
        name: name,
        value: value,
        type: 'checkbox',
        checked: checked
      }
    };
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Filter>
        <FilterContent>
          <FilterInput
            label={t('materials.search')}
            endIcon={FiSearch}
            placeholder={t('filter.searchActivity')}
            type="search"
            name="activitySearch"
            id="activitySearch"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />

          <FilterSelectNative
            label={t('filter.labelSelectCourse')}
            value={filters.values.course}
            onChange={e => {
              filters.setFieldValue('course', e.target.value);
              filters.setFieldValue('classroom', 'blank');
            }}
            options={relationships.courses.map(c => ({
              ...c,
              name: c.title
            }))}
          />

          <FilterSelectNative
            label={t('filter.labelSelectClassroom')}
            value={filters.values.classroom}
            onChange={e => {
              filters.setFieldValue('classroom', e.target.value);
              filters.setFieldValue('course', 'blank');
            }}
            options={relationships.classrooms.map(c => ({
              ...c,
              name: c.title
            }))}
          />

          <FilterCheck
            label={t('filter.pendingEvaluation')}
            name="activity-status"
            id="activity-status"
            checked={filters.values['activity-status']}
            onCheckedChange={checked => {
              const syntheticEvent = createSyntheticChangeEvent(checked, 'activity-status');
              filters.handleChange(syntheticEvent);
            }}
          />
        </FilterContent>

        <FilterActions>
          {usePermission('Criar atividades', 'Arquivos e atividades complementares') && (
            <Sheet
              open={showModal}
              onOpenChange={setShowModal}
            >
              <SheetTrigger asChild>
                <Button
                  wide
                  onClick={newActivity}
                  type="button"
                >
                  Nova Atividade
                </Button>
              </SheetTrigger>

              <SheetContent>
                <SheetTitle className="sr-only">Nova Atividade</SheetTitle>
                <SheetBody className="py-6">
                  <ActivityForm
                    onClose={() => setShowModal(false)}
                    editingActivity={editingActivity}
                    activities={activities}
                    setActivities={setActivities}
                  />
                </SheetBody>
              </SheetContent>
            </Sheet>
          )}
        </FilterActions>
      </Filter>

      {loading && <Loader />}

      {!loading && !filteredActivities.length && <EmptyState type="data" />}

      {!loading && filteredActivities.length > 0 && (
        <CardWrapper>
          {filteredActivities.map((activity, i) => (
            <ComplementaryActivityCard
              admin={true}
              authorityLevel={authorityLevel}
              key={i}
              activity={activity}
              editActivity={() => editActivity(activity)}
              onDelete={() => deleteActivity(activity.id)}
            />
          ))}
        </CardWrapper>
      )}

      <Onboarding
        path={path}
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        profiles={profiles}
        title={t('onboarding.titleActivities')}
        videoId="l-9Am7IbsEY"
      />
    </DashboardContent>
  );
}
